// ─────────────────────────────────────────────────────────────────────────────
//  LicenseGate
//
//  Wraps the entire app in App.js. Before anything else renders — the login
//  screen included — it asks the server whether this facility's subscription
//  is in date. If it is not, the app is never mounted: only ServiceSuspended
//  is shown, and it stays.
//
//  When it re-checks:
//   • on first load;
//   • every 10 minutes, so a session left open past midnight on the expiry
//     date is locked without needing a reload;
//   • whenever any request comes back 403 { licenseBlocked } WITHOUT a
//     `feature` field — that is the domain licence (login, requireDomain).
//     AI / templates refusals carry `feature` and are ignored here.
//
//  FAILS OPEN, like the endpoint: if the check errors or times out the app
//  loads normally. The server's /login still refuses an expired facility, so
//  failing open costs nothing but a less pretty refusal.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { systemLicenseStatus } from '../BaseURL';
import { BRAND } from '../../brand';
import ServiceSuspended from './ServiceSuspended';

const RECHECK_MS = 10 * 60 * 1000;
const TIMEOUT_MS = 8000;

const LicenseGate = ({ children }) => {
  const [status, setStatus]     = useState(null);
  const [ready, setReady]       = useState(false);
  const [checking, setChecking] = useState(false);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setChecking(true);
    try {
      const { data } = await axios.get(systemLicenseStatus, { timeout: TIMEOUT_MS });
      setStatus(data && data.blocked ? data : null);
    } catch (err) {
      console.warn('[licence-gate] status check failed, letting the app load:', err.message);
    } finally {
      inFlight.current = false;
      setChecking(false);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    check();
    const t = setInterval(check, RECHECK_MS);
    return () => clearInterval(t);
  }, [check]);

  // A domain-licence refusal from anywhere in the app means: look again now.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const d = err?.response?.data;
        if (err?.response?.status === 403 && d?.licenseBlocked && !d?.feature) check();
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [check]);

  // The login screen pops its own "licence expired" alert on the same 403.
  // Once this screen is up, that alert is redundant — close it.
  useEffect(() => {
    if (status) Swal.close();
  }, [status]);

  if (!ready) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BRAND.canvas }}>
        <CircularProgress size={34} sx={{ color: BRAND.navy }} />
      </Box>
    );
  }

  if (status) return <ServiceSuspended status={status} onRetry={check} checking={checking} />;

  return children;
};

export default LicenseGate;
